//src/components/ui/modal/Modal.tsx
'use client';

import React, { createContext, useContext, useEffect, useId, useMemo } from 'react';
import clsx from 'clsx';

type ModalContext = {
  close: () => void;
  labelledBy: string;
};

const Ctx = createContext<ModalContext | null>(null);

export function useModalCtx() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useModalCtx must be used within <Modal.Root>');
  return ctx;
}

export type ModalRootProps = {
  open: boolean;
  onClose: () => void;
  closeOnBackdrop?: boolean;
  lockScroll?: boolean;
  className?: string;
  children?: React.ReactNode;
};

export const Root: React.FC<ModalRootProps> = ({
  open,
  onClose,
  closeOnBackdrop = true,
  lockScroll = true,
  className,
  children,
}) => {
  const labelledBy = useId();

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (!open || !lockScroll) return;
    const { style, classList } = document.body;
    const prevOverflow = style.overflow;
    classList.add('modal-open');
    style.overflow = 'hidden';
    return () => {
      classList.remove('modal-open');
      style.overflow = prevOverflow;
    };
  }, [open, lockScroll]);

  const ctx = useMemo<ModalContext>(() => ({ close: onClose, labelledBy }), [onClose, labelledBy]);

  if (!open) return null;

  return (
    <Ctx.Provider value={ctx}>
      <div className="modal-backdrop fade show" onClick={closeOnBackdrop ? onClose : undefined} />
      <div
        className={clsx('modal fade show d-block', className)}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        onClick={closeOnBackdrop ? onClose : undefined}
      >
        {children}
      </div>
    </Ctx.Provider>
  );
};

export type ModalDialogProps = {
  size?: 'sm' | 'md' | 'lg' | 'xl';
  centered?: boolean;
  scrollable?: boolean;
  className?: string;
  children?: React.ReactNode;
};

export const Dialog: React.FC<ModalDialogProps> = ({
  size,
  centered,
  scrollable,
  className,
  children,
}) => {
  return (
    <div
      className={clsx(
        'modal-dialog px-2',
        centered && 'modal-dialog-centered',
        scrollable && 'modal-dialog-scrollable',
        size === 'sm' && 'modal-sm',
        size === 'md' && 'modal-md',
        size === 'lg' && 'modal-lg',
        size === 'xl' && 'modal-xl',
        className
      )}
      onClick={(e) => e.stopPropagation()}
    >
      {children}
    </div>
  );
};

export const Content: React.FC<React.ComponentProps<'div'>> = ({ className, ...rest }) => (
  <div className={clsx('modal-content shadow-sm rounded-4 border-0', className)} {...rest} />
);

export const Title: React.FC<React.ComponentProps<'h5'>> = ({ className, ...rest }) => {
  const { labelledBy } = useModalCtx();
  return <h5 id={labelledBy} className={clsx('mb-0', className)} {...rest} />;
};
